import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { cn } from "@/lib/utils";
import { groupCode } from "@/lib/format";

const PILL = {
  connected: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  needs_attention: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  disconnected: "bg-rose-500/10 text-rose-400 border-rose-500/30",
  neutral: "bg-zinc-800/80 text-zinc-400 border-zinc-700/50",
};
const PILL_LABEL = { connected: "Connected", needs_attention: "Needs attention", disconnected: "Not connected" };

export function StatusPill({ status = "disconnected", label, className, ...props }) {
  const key = PILL[status] ? status : "neutral";
  return (
    <span
      className={cn("inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold", PILL[key], className)}
      {...props}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full bg-current", status === "needs_attention" && "animate-pulse")} />
      {label || PILL_LABEL[status] || status}
    </span>
  );
}

const SRC = {
  openphone: "bg-blue-500/10 text-blue-400 border-blue-500/25",
  gmail: "bg-red-500/10 text-red-400 border-red-500/25",
  test: "bg-zinc-700/40 text-zinc-300 border-zinc-600/40",
};
const SRC_LABEL = { openphone: "OpenPhone", gmail: "Gmail", test: "Test" };

export function SourceBadge({ type, className, ...props }) {
  return (
    <span className={cn("inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider", SRC[type] || SRC.test, className)} {...props}>
      {SRC_LABEL[type] || type}
    </span>
  );
}

export function CodeCell({ code, dim, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(code); } catch { /* ignore */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };
  return (
    <button
      type="button"
      onClick={copy}
      data-testid={testId}
      title="Copy code"
      className={cn(
        "group inline-flex items-center gap-2 rounded-md border px-2.5 py-1 transition-colors",
        copied ? "border-emerald-500/60 bg-emerald-500/10" : "border-[#242A36] bg-[#171C26] hover:border-emerald-500/40",
        dim && "opacity-50",
      )}
    >
      <span className="code-digits text-base">{groupCode(code)}</span>
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5 text-zinc-500 group-hover:text-zinc-300" />}
    </button>
  );
}

export function Empty({ title, children, testId }) {
  return (
    <div data-testid={testId} className="surface flex flex-col items-start gap-2 p-8">
      <p className="font-display text-base font-semibold text-zinc-200">{title}</p>
      <div className="text-sm text-zinc-400">{children}</div>
    </div>
  );
}
